/**
 * Custom authentication modal using Netlify GoTrue API
 * Provides sign-up and sign-in flows without relying on Netlify Identity widget
 */

import {
  GOTRUE_API_URL,
  initIdentityWidget,
  loadIdentityWidget,
  loadIdentityWidgetIfTokenPresent,
} from './identity-widget.js';
import { saveSession, clearSession, currentEmail, signOut, ensureFreshSession } from './lib/session.js';

export { initIdentityWidget };

// Re-exported for the pages that have always imported it from here. The
// implementation moved to lib/session.js, which owns the stored session.
export { ensureFreshSession };

export class AuthModal {
  constructor(options = {}) {
    this.apiUrl = options.apiUrl || GOTRUE_API_URL;
    this.mode = 'login'; // 'login' or 'signup'
    this.isLoading = false;
    this._onLoginCallbacks = [];
    this.setupDOM();
    this.attachListeners();
  }

  setupDOM() {
    const html = `
      <dialog class="auth-modal" id="auth-modal" aria-labelledby="auth-title">
        <div class="auth-modal-content">
          <div class="auth-modal-header">
            <h2 id="auth-title">Sign In</h2>
            <button class="auth-modal-close" id="auth-close" aria-label="Close">✕</button>
          </div>

          <!-- Tab switcher -->
          <div class="auth-tabs" role="tablist">
            <button class="auth-tab-btn auth-tab-btn--active" data-mode="login" role="tab" aria-selected="true">
              Sign In
            </button>
            <button class="auth-tab-btn" data-mode="signup" role="tab" aria-selected="false">
              Create Account
            </button>
          </div>

          <!-- Form -->
          <form id="auth-form" class="auth-form" novalidate>
            <div class="form-group">
              <label for="auth-email">Email</label>
              <input
                type="email"
                id="auth-email"
                name="email"
                required
                placeholder="your@email.com"
                autocomplete="email">
            </div>

            <div class="form-group">
              <label for="auth-password">Password</label>
              <input
                type="password"
                id="auth-password"
                name="password"
                required
                placeholder="••••••••"
                autocomplete="current-password">
            </div>

            <!-- Signup-only: confirm password -->
            <div class="form-group" id="confirm-password-group" hidden>
              <label for="auth-confirm">Confirm Password</label>
              <input
                type="password"
                id="auth-confirm"
                name="confirm"
                placeholder="••••••••"
                autocomplete="new-password">
              <span class="form-hint" id="password-hint" hidden>Passwords don't match</span>
            </div>

            <button type="submit" class="btn btn-primary auth-submit-btn" id="auth-submit">
              Sign In
            </button>

            <p id="auth-error" class="auth-error" role="alert" aria-live="polite" hidden></p>
            <p id="auth-loading" class="auth-loading" hidden aria-live="polite">Loading…</p>
            <p id="auth-success" class="auth-success" role="status" aria-live="polite" hidden></p>
          </form>

          <!-- Password reset link (login mode only) -->
          <p id="forgot-password-section" class="auth-forgot">
            <button type="button" id="forgot-password-btn" class="auth-link-btn">
              Forgot password?
            </button>
          </p>

          <!-- Sign-up info (signup mode only) -->
          <p id="signup-info" class="auth-info" hidden>
            We'll send a confirmation email. Check your inbox to verify your account.
          </p>
        </div>
      </dialog>
    `;

    document.body.insertAdjacentHTML('beforeend', html);
  }

  attachListeners() {
    const tabBtns   = document.querySelectorAll('.auth-tab-btn');
    const form      = document.getElementById('auth-form');
    const closeBtn  = document.getElementById('auth-close');
    const forgotBtn = document.getElementById('forgot-password-btn');
    const passwordInputs = [
      document.getElementById('auth-password'),
      document.getElementById('auth-confirm'),
    ];

    // Tab switching
    tabBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        this.switchMode(btn.dataset.mode);
      });
    });

    // Form submission
    form.addEventListener('submit', e => {
      e.preventDefault();
      this.handleSubmit();
    });

    // Real-time password match validation (signup only)
    passwordInputs.forEach(input => {
      input.addEventListener('input', () => {
        this.validatePasswordMatch();
      });
    });

    // Close button
    closeBtn.addEventListener('click', () => {
      this.close();
    });


    // Close on Escape key

    // Forgot password
    forgotBtn.addEventListener('click', e => {
      e.preventDefault();
      this.showPasswordReset();
    });
  }

  switchMode(mode) {
    this.mode = mode;

    const tabBtns        = document.querySelectorAll('.auth-tab-btn');
    const title          = document.getElementById('auth-title');
    const submitBtn      = document.getElementById('auth-submit');
    const confirmGroup   = document.getElementById('confirm-password-group');
    const forgotSection  = document.getElementById('forgot-password-section');
    const signupInfo     = document.getElementById('signup-info');
    const passwordHint   = document.getElementById('password-hint');

    // Update tab styling and aria
    tabBtns.forEach(btn => {
      const isActive = btn.dataset.mode === mode;
      btn.classList.toggle('auth-tab-btn--active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });

    if (mode === 'signup') {
      title.textContent    = 'Create Account';
      submitBtn.textContent = 'Sign Up';
      confirmGroup.hidden  = false;
      forgotSection.hidden = true;
      signupInfo.hidden    = false;
      document.getElementById('auth-password').autocomplete = 'new-password';
    } else {
      title.textContent    = 'Sign In';
      submitBtn.textContent = 'Sign In';
      confirmGroup.hidden  = true;
      forgotSection.hidden = false;
      signupInfo.hidden    = true;
      passwordHint.hidden  = true;
      document.getElementById('auth-password').autocomplete = 'current-password';
    }

    // Clear form and messages
    document.getElementById('auth-form').reset();
    this.clearError();
    this.clearSuccess();
  }

  validatePasswordMatch() {
    if (this.mode !== 'signup') return;

    const password = document.getElementById('auth-password').value;
    const confirm  = document.getElementById('auth-confirm').value;
    const hint     = document.getElementById('password-hint');

    if (confirm && password !== confirm) {
      hint.hidden = false;
    } else {
      hint.hidden = true;
    }
  }

  async handleSubmit() {
    const email    = document.getElementById('auth-email').value.trim();
    const password = document.getElementById('auth-password').value;
    const confirm  = document.getElementById('auth-confirm').value;

    // Basic validation
    if (!email || !password) {
      this.showError('Email and password are required');
      return;
    }

    if (this.mode === 'signup' && !confirm) {
      this.showError('Please confirm your password');
      return;
    }

    if (this.mode === 'signup' && password !== confirm) {
      this.showError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      this.showError('Password must be at least 6 characters');
      return;
    }

    this.clearError();
    this.clearSuccess();

    const submitBtn = document.getElementById('auth-submit');
    const loadingEl = document.getElementById('auth-loading');

    submitBtn.disabled  = true;
    loadingEl.hidden    = false;
    this.isLoading      = true;

    try {
      if (this.mode === 'signup') {
        await this.signup(email, password);
      } else {
        await this.login(email, password);
      }
    } catch (err) {
      this.showError(err.message);
    } finally {
      submitBtn.disabled = false;
      loadingEl.hidden   = true;
      this.isLoading     = false;
    }
  }

  async signup(email, password) {
    const response = await fetch(`${this.apiUrl}/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error_description || data.msg || 'Sign-up failed');
    }

    this.showSuccess(
      'Account created! Check your email to confirm, then sign in.'
    );

    // Switch back to login after a short delay so the user can sign in
    setTimeout(() => {
      this.switchMode('login');
    }, 2000);
  }

  async login(email, password) {
    // GoTrue's /token endpoint is an OAuth 2.0 token endpoint and requires
    // application/x-www-form-urlencoded, NOT application/json.
    // Sending JSON causes a "unsupported grant_type" error because the server
    // cannot parse the grant_type field from a JSON body.
    const params = new URLSearchParams();
    params.append('grant_type', 'password');
    params.append('username', email);
    params.append('password', password);

    const response = await fetch(`${this.apiUrl}/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error_description || data.msg || 'Sign-in failed');
    }

    const data = await response.json();

    saveSession({ ...data, email });

    this.showSuccess('Signed in successfully!');

    // Dispatch a custom event so board.js (and any other listeners) can react
    // without a full page reload.
    window.dispatchEvent(
      new CustomEvent('auth-modal:login', {
        detail: {
          token: data.access_token,
          email,
          user: { email, token: data.access_token },
        },
      })
    );

    // Close modal after a short pause so the success message is readable
    setTimeout(() => {
      this.close();
    }, 800);
  }

  showError(message) {
    const errorEl = document.getElementById('auth-error');
    errorEl.textContent = message;
    errorEl.hidden = false;
    document.getElementById('auth-success').hidden = true;
  }

  clearError() {
    const errorEl = document.getElementById('auth-error');
    errorEl.textContent = '';
    errorEl.hidden = true;
  }

  showSuccess(message) {
    const successEl = document.getElementById('auth-success');
    successEl.textContent = message;
    successEl.hidden = false;
    document.getElementById('auth-error').hidden = true;
  }

  clearSuccess() {
    const successEl = document.getElementById('auth-success');
    successEl.textContent = '';
    successEl.hidden = true;
  }

  async showPasswordReset() {
    // GoTrue recovery only exists as a widget flow, and the widget is no longer
    // on every page — it is 481ms of third-party download for something almost
    // no page view uses. Fetch it at the moment someone asks to reset, which is
    // the only moment it is needed.
    this.showError('');
    await loadIdentityWidget();

    if (window.netlifyIdentity) {
      this.close();
      window.netlifyIdentity.open('recovery');
    } else {
      // Blocked, offline, or the script failed. Same message as before.
      this.showError(
        'To reset your password, visit the sign-in page and use "Forgot password" in the Netlify Identity dialog.'
      );
    }
  }

  open(initialMode = 'login') {
    this.switchMode(initialMode);
    /** @type {HTMLDialogElement} */
    const dialog = document.getElementById('auth-modal');
    if (!dialog.open) dialog.showModal();
    // showModal() focuses the first focusable child, which is the close
    // button; the email field is where someone actually wants to start.
    document.getElementById('auth-email').focus();
  }

  close() {
    /** @type {HTMLDialogElement} */
    const dialog = document.getElementById('auth-modal');
    if (dialog.open) dialog.close();
    document.getElementById('auth-form').reset();
    this.clearError();
    this.clearSuccess();
  }

  /**
   * Register a callback to run after a successful login.
   * The callback receives { token, email, user }.
   */
  onLogin(fn) {
    window.addEventListener('auth-modal:login', e => fn(e.detail));
  }
}

// ── Singleton ─────────────────────────────────────────────────────────────────
export const authModal = new AuthModal({
  apiUrl: GOTRUE_API_URL,
});


// ── Auth bar (header sign-out strip) ──────────────────────────────────────────
/**
 * Wire up the persistent auth bar that appears in the page header when a user
 * is signed in.  Expects the following elements in the DOM:
 *
 *   <div id="auth-bar" hidden>
 *     <span id="auth-bar-email"></span>
 *     <button id="auth-bar-logout">Sign Out</button>
 *   </div>
 *
 * Works with both the Netlify Identity widget session and the custom-modal
 * session stored in localStorage under `gotrue.user`.
 */
export function initAuthBar() {
  // Initialize the widget regardless of whether this page has auth-bar
  // markup — see initIdentityWidget() for why this has to run everywhere.
  initIdentityWidget();

  const authBar   = document.getElementById('auth-bar');
  const emailEl   = document.getElementById('auth-bar-email');
  const logoutBtn = document.getElementById('auth-bar-logout');

  if (!authBar || !logoutBtn) return;

  const resolveUser = () => {
    const email = currentEmail();
    return email ? { email } : null;
  };

  function applyUser(user) {
    if (user) {
      if (emailEl) emailEl.textContent = user.email;
      authBar.hidden = false;
    } else {
      if (emailEl) emailEl.textContent = '';
      authBar.hidden = true;
    }
  }

  // A token link can land on any page, not only the site root — the Netlify
  // email template is editable, and a confirmation link that silently does
  // nothing is a miserable thing to diagnose. Costs one hash check when there
  // is no token, which is every ordinary page view.
  loadIdentityWidgetIfTokenPresent().then(() => {
    if (window.netlifyIdentity) attachWidgetEvents();
  });

  // Initial render
  applyUser(resolveUser());

  // ── Netlify Identity widget events ────────────────────────────────────────
  // A function rather than a straight-line block, because the widget is now
  // loaded on demand and may arrive after this runs.
  function attachWidgetEvents() {
    const identity = window.netlifyIdentity;
    if (!identity) return;
    identity.on('init',   user => applyUser(user ? { email: user.email } : resolveUser()));
    identity.on('login',  user => applyUser({ email: user.email }));
    identity.on('logout', ()   => {
      // The widget's own session is gone; clear the custom-modal one too.
      clearSession();
      applyUser(null);
    });
  }

  attachWidgetEvents();

  // ── Custom auth-modal login event ─────────────────────────────────────────
  window.addEventListener('auth-modal:login', e => {
    applyUser({ email: e.detail.email });
  });

  // ── Sign-out button ───────────────────────────────────────────────────────
  logoutBtn.addEventListener('click', () => {
    const hadWidgetSession = Boolean(window.netlifyIdentity?.currentUser?.());
    signOut();
    // With a widget session the 'logout' handler above repaints; without one
    // nothing else will, so do it here.
    if (!hadWidgetSession) applyUser(null);
  });
}
